/**
 * Application Constants
 */

// Roles
export const ROLES = {
  GUEST: 'guest',
  VIEWER: 'viewer',
  CONTRIBUTOR: 'contributor',
  ANALYST: 'analyst',
  MANAGER: 'manager',
  ADMIN: 'admin',
} as const;

export type Role = typeof ROLES[keyof typeof ROLES];

export const ROLE_PRIORITY: Record<Role, number> = {
  guest: 0,
  viewer: 10,
  contributor: 30,
  analyst: 50,
  manager: 70,
  admin: 100,
};

// Pagination
export const PAGINATION = {
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
  PLOTS_LIMIT: 1000,
  CONCESSIONS_LIMIT: 500,
} as const;

// Cache times (in milliseconds)
export const CACHE_TIMES = {
  STALE_TIME: 5 * 60 * 1000,      // 5 minutes
  GC_TIME: 10 * 60 * 1000,        // 10 minutes
  REFERENCES_STALE: 60 * 60 * 1000, // 1 hour
} as const;

// Breakpoints (matching Tailwind)
export const BREAKPOINTS = {
  SM: 640,
  MD: 768,
  LG: 1024,
  XL: 1280,
  '2XL': 1536,
} as const;

// Layout sizes
export const LAYOUT = {
  SIDEBAR_WIDTH: 400,
  SIDEBAR_COLLAPSED_WIDTH: 0,
  HEADER_HEIGHT: 56,
  MOBILE_SHEET_MIN: 120,
  MOBILE_SHEET_HALF: '50vh',
  MOBILE_SHEET_FULL: '90vh',
} as const;

// REINFO statuses
export const REINFO_ESTADOS = {
  VIGENTE: 'VIGENTE',
  SUSPENDIDO: 'SUSPENDIDO',
} as const;

export type ReinfoEstado = typeof REINFO_ESTADOS[keyof typeof REINFO_ESTADOS];

// Catastro leyendas
export const CATASTRO_LEYENDAS = {
  VIGENTE: 'VIGENTE',
  EXTINTO: 'EXTINTO',
  BLOQUEADO: 'BLOQUEADO',
} as const;

export type CatastroLeyenda = typeof CATASTRO_LEYENDAS[keyof typeof CATASTRO_LEYENDAS];

// Owner types
export const OWNER_TYPES = {
  PERSON: 'PERSON',
  COMPANY: 'COMPANY',
} as const;

export type OwnerType = typeof OWNER_TYPES[keyof typeof OWNER_TYPES];

// Tipo persona (from REINFO)
export const TIPO_PERSONA = {
  NATURAL: 'Natural',
  JURIDICA: 'Jurídica',
} as const;

export type TipoPersona = typeof TIPO_PERSONA[keyof typeof TIPO_PERSONA];

// Tipo actividad
export const TIPO_ACTIVIDAD = {
  EXPLOTACION: 'EXPLOTACION',
  BENEFICIO: 'BENEFICIO',
} as const;

export type TipoActividad = typeof TIPO_ACTIVIDAD[keyof typeof TIPO_ACTIVIDAD];

// Note categories
export const NOTE_CATEGORIES = {
  OBSERVATION: 'observation',
  WARNING: 'warning',
  TODO: 'todo',
  OTHER: 'other',
} as const;

export type NoteCategory = typeof NOTE_CATEGORIES[keyof typeof NOTE_CATEGORIES];

// Note importance
export const NOTE_IMPORTANCE = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
} as const;

export type NoteImportance = typeof NOTE_IMPORTANCE[keyof typeof NOTE_IMPORTANCE];

// Job statuses
export const JOB_STATUSES = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

export type JobStatus = typeof JOB_STATUSES[keyof typeof JOB_STATUSES];

// Export formats
export const EXPORT_FORMATS = {
  CSV: 'csv',
  GEOJSON: 'geojson',
  SHAPEFILE: 'shapefile',
  EXCEL: 'excel',
} as const;

export type ExportFormat = typeof EXPORT_FORMATS[keyof typeof EXPORT_FORMATS];

// Match types for owner matching
export const MATCH_TYPES = {
  RUC_EXACT: 'RUC_EXACT',
  DNI_FROM_RUC: 'DNI_FROM_RUC',
  NAME_FUZZY: 'NAME_FUZZY',
} as const;

export type MatchType = typeof MATCH_TYPES[keyof typeof MATCH_TYPES];

// Relation types
export const RELATION_TYPES = {
  COMPANY: 'company',
  ADDRESS: 'address',
  FAMILY: 'family',
  PHONE: 'phone',
} as const;

export type RelationType = typeof RELATION_TYPES[keyof typeof RELATION_TYPES];

// Enrichment types
export const ENRICHMENT_TYPES = [
  { id: 'satellite', name: 'Спутниковые снимки' },
  { id: 'sunat', name: 'Данные SUNAT' },
  { id: 'oefa', name: 'Данные OEFA' },
  { id: 'sunarp', name: 'Данные SUNARP' },
  { id: 'deforestation', name: 'Анализ обезлесения' },
  { id: 'mining_activity', name: 'Анализ горнодобычи' },
  { id: 'water_bodies', name: 'Анализ водных объектов' },
  { id: 'news', name: 'Упоминания в СМИ' },
] as const;

// Satellite image types
export const SATELLITE_IMAGE_TYPES = {
  RGB: 'RGB',
  NDVI: 'NDVI',
  NDWI: 'NDWI',
  FALSE_COLOR: 'FALSE_COLOR',
} as const;

export type SatelliteImageType = typeof SATELLITE_IMAGE_TYPES[keyof typeof SATELLITE_IMAGE_TYPES];

// Satellite sources
export const SATELLITE_SOURCES = {
  SENTINEL_2: 'Sentinel-2',
  LANDSAT_8: 'Landsat-8',
  PLANET: 'Planet',
} as const;

export type SatelliteSource = typeof SATELLITE_SOURCES[keyof typeof SATELLITE_SOURCES];
